import { useEffect, useRef, useState } from "react";

/**
 * A universal hook for managing a boolean state with:
 *  - delayed activation (debounce before turning true)
 *  - minimum active duration (lock before turning false)
 *
 * Example:
 * const isVisible = useStagedBoolean(isLoading, 500, 1000);
 * // -> becomes true after 0.5s delay
 * // -> stays true for at least 1s even if isLoading becomes false earlier
 *
 * @param active       Incoming boolean flag (e.g., "isLoading")
 * @param delayMs      Delay before setting state to true (default: 500ms)
 * @param minActiveMs  Minimum time to keep state true (default: 0ms)
 */
export function useStagedBoolean(
  active: boolean,
  delayMs = 500,
  minActiveMs = 0,
) {
  const [state, setState] = useState(false);

  const activateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deactivateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activatedAt = useRef<number | null>(null);

  const clearTimer = (
    ref: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  ) => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  };

  useEffect(() => {
    if (active) {
      // cancel any pending deactivation
      clearTimer(deactivateTimer);

      // if not yet active, schedule activation after delay
      if (!state && !activateTimer.current) {
        activateTimer.current = setTimeout(() => {
          setState(true);
          activatedAt.current = Date.now();
          activateTimer.current = null;
        }, delayMs);
      }
    } else {
      // cancel any pending activation
      clearTimer(activateTimer);

      if (state) {
        // determine how long it's been active
        const elapsed = activatedAt.current
          ? Date.now() - activatedAt.current
          : 0;
        const remaining = Math.max(0, minActiveMs - elapsed);

        if (remaining > 0) {
          // enforce minimum visible duration
          if (!deactivateTimer.current) {
            deactivateTimer.current = setTimeout(() => {
              setState(false);
              activatedAt.current = null;
              deactivateTimer.current = null;
            }, remaining);
          }
        } else {
          // safe to deactivate immediately
          setState(false);
          activatedAt.current = null;
        }
      }
    }

    // cleanup on unmount or dependency change
    return () => {
      clearTimer(activateTimer);
      clearTimer(deactivateTimer);
    };
  }, [active, delayMs, minActiveMs, state]);

  return state;
}
